import React, { useState } from 'react';
import * as MuiMaterial from '@mui/material';
import { FieldProps } from '@rjsf/core';
import { getCountryCodeFromLanguageCode } from '@askporter/utils';
import { Accordion } from '../../stories/Accordion';
import { TextField } from '../../stories/TextField';
import type { CustomWidgetProps } from '../types';

const { Divider, InputAdornment, Box, Typography } = MuiMaterial;

interface MultiLangFieldState {
  hasChanged: boolean;
  data: Record<string, string>;
  lastChaged?: string;
}

const MultiLangField = ({
  formData = {},
  idSchema,
  schema,
  onChange,
  onBlur,
  onFocus,
  disabled,
  readonly,
  options,
  formContext,
}: FieldProps): JSX.Element => {
  const { onHasChanged, t, tenantLanguages }: CustomWidgetProps['formContext'] = formContext;
  const tenantDefaultLang = (tenantLanguages || []).find((lang) => lang.defaultLanguage);
  const id = idSchema?.$id;

  const initialState = tenantLanguages?.reduce((prev, curr) => {
    return { ...prev, [curr?.languageCode]: formData?.[curr?.languageCode] };
  }, {});

  const [state, setState] = useState<MultiLangFieldState>({
    hasChanged: false,
    data: initialState,
    lastChaged: undefined,
  });

  const _onChange = (value: string, languageCode: string) => {
    const newData = { ...(state.data || {}), [languageCode]: value };
    onChange(newData);

    setState({ ...state, data: newData, hasChanged: true });
  };

  const _onBlur = (value: string, languageCode: string) => {
    state.hasChanged && onHasChanged?.({ id, value });
    onBlur(id, value);

    setState({ ...state, lastChaged: languageCode, hasChanged: false });
  };

  const _onFocus = ({ target: { value } }: React.FocusEvent<HTMLInputElement>) => onFocus(id, value);

  return (
    <Box>
      <Box>
        <Typography variant="h3" component="h3">
          {schema?.title}
        </Typography>
      </Box>
      {tenantDefaultLang?.languageCode && formData[tenantDefaultLang?.languageCode] && (
        <Box sx={{ mt: '20px' }}>
          <TextField
            fullWidth
            label={tenantDefaultLang?.languageName}
            value={tenantDefaultLang?.languageCode ? formData[tenantDefaultLang.languageCode] : ''}
            multiline={true}
            minRows={(options?.rows as number) || 3}
            slotProps={{
              htmlInput: {
                readOnly: true,
              },
            }}
          />
        </Box>
      )}
      <Divider sx={{ mt: 6 }} />
      <Accordion
        idPrefix="multi-lang-field"
        title={t?.('common:form:multi_lang:accordion_header') || ''}
        content={
          <>
            {(tenantLanguages || []).map(({ languageCode, languageName }) => (
              <TextField
                fullWidth
                key={languageCode}
                label={languageName}
                disabled={disabled}
                value={state.data?.[languageCode] || formData?.[languageCode]}
                multiline={true}
                minRows={(options?.rows as number) || 3}
                onChange={(e) => _onChange(e?.target?.value, languageCode)}
                onBlur={(e) => _onBlur(e?.target?.value, languageCode)}
                onFocus={_onFocus}
                sx={{ mb: '20px' }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <img
                          width={32}
                          alt={`flag-${languageCode}`}
                          src={`/media/images/shared/flags/${getCountryCodeFromLanguageCode(languageCode)}.png`}
                        />
                      </InputAdornment>
                    ),
                  },
                  htmlInput: {
                    readOnly: readonly,
                    'aria-label': languageName,
                  },
                }}
              />
            ))}
          </>
        }
      />
    </Box>
  );
};

export default MultiLangField;
